import { useSearchParams } from "react-router-dom";
import { useMemo, useCallback } from "react";

/**
 * `useSearchParams` をラップし、より扱いやすいインターフェースを提供するカスタムフック。
 * URLの検索パラメータをプレーンなオブジェクトとして提供し、安全な更新関数を提供します。
 */
export const useURLSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * 現在の検索パラメータを安定したオブジェクトとしてメモ化して提供します。
   * 例: `?name=test&page=1` -> `{ name: 'test', page: '1' }`
   */
  const urlParams = useMemo(() => {
    return Object.fromEntries(searchParams.entries());
  }, [searchParams]);

  /**
   * 検索パラメータを更新するための安定した関数。
   * オブジェクトを受け取り、既存のパラメータとマージします。
   * 値が `null` または `undefined` の場合、そのパラメータは削除されます。
   * @param updates - 更新するパラメータのオブジェクト
   * @param replace - (オプション) trueの場合、既存のパラメータを全て置き換える
   */
  const updateSearchParams = useCallback(
    (updates: Record<string, any>, replace = false) => {
      const newParams = replace ? new URLSearchParams() : new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  return { urlParams, updateSearchParams };
};
